import i18n from '@/utils/i18n';
import React from 'react';
import { createStyles } from '@/contexts/theme-context';
import { Typography } from '@usepepper/ui/atoms/typography';
import { View } from 'react-native';

interface Props {
  /** Slot beneath the copy — the order review screen puts its Enroll button here. */
  children?: React.ReactNode;
  /**
   * Whether the restaurant is already enrolled. Drives both the copy and the
   * colour: enrolled reads as earned (green, with an "Enrolled" tag), not
   * enrolled reads as pending action (yellow).
   */
  enrolled: boolean;
  estimatedPoints: number;
  loading: boolean;
  programName: string;
}

/**
 * The loyalty panel on the order review screen: the points this order earns,
 * shown large, with a line of context underneath and an optional action slot.
 */
export const LoyaltyPointsLine: React.FC<Props> = ({
  children,
  enrolled,
  estimatedPoints,
  loading,
  programName,
}) => {
  const [styles, theme] = useStyles();

  const accentColor = enrolled
    ? theme.sk.color.text.success
    : theme.sk.color.text.warning;

  return (
    <View style={[styles.base, !enrolled && styles.warningBackground]}>
      <View style={styles.headerRow}>
        {/* Bare figure — "points" lives in the caption below, alongside the
            program name, so the two read as one sentence. */}
        <Typography
          color={accentColor}
          testID="loyalty-points-line.value"
          variant="heading-md"
        >
          {loading
            ? '--'
            : i18n.t('loyalty.points', {
                points: estimatedPoints.toLocaleString(i18n.locale),
              })}
        </Typography>
        {enrolled ? (
          <Typography
            color={theme.sk.color.text.success}
            variant="paragraph-sm-600"
          >
            {i18n.t('loyalty.enrolled')}
          </Typography>
        ) : null}
      </View>
      <Typography color={theme.sk.color.text.secondary} variant="paragraph-sm">
        {enrolled
          ? i18n.t('loyalty.points_on_this_order', { programName })
          : i18n.t('loyalty.points_waiting', { programName })}
      </Typography>
      {children}
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  base: {
    backgroundColor: theme.sk.color.bg.highlight.secondary,
    borderRadius: theme.sk.radii.radiusMedium,
    gap: theme.sk.spacing.spaceXsmall,
    paddingHorizontal: theme.sk.spacing.spaceMedium,
    paddingVertical: theme.sk.spacing.spaceSmall,
  },
  headerRow: {
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  warningBackground: {
    backgroundColor: theme.sk.color.bg.warning,
  },
}));
